import React, { useContext, useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import "./Announcements.css";
import Announcement from "./Announcement";
import { HerracContext } from "../../App";
import useInfiniteScroll from "../useInfiniteScroll";
import Loading from "../Loading";
import { 
  FaSearch, 
  FaGavel, 
  FaFilter, 
  FaSortAmountDown, 
  FaTh, 
  FaThList,
  FaChevronDown,
  FaMapMarkerAlt,
  FaDollarSign,
  FaTimes,
  FaCheckCircle
} from "react-icons/fa";

// Formatlaşdırma funksiyaları
const capitalizeFirstLetter = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const formatCategoryName = (str) => {
  if (!str) return "";
  return capitalizeFirstLetter(str.replace(/-/g, " "));
};

const formatUrlString = (str) => {
  if (!str) return "";
  const azerbaijaniMap = {
    "ə": "e", "ş": "s", "ğ": "g", "ü": "u", "ö": "o", "ç": "c", "ı": "i",
    "Ə": "E", "Ş": "S", "Ğ": "G", "Ü": "U", "Ö": "O", "Ç": "C", "I": "I"
  };
  return str
    .toLowerCase()
    .replace(/[əşıöğüçƏŞİÖĞÜÇ]/g, match => azerbaijaniMap[match] || match)
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "");
};

function Announcements() {
  const {
    productsData,
    selectedCategory,
    selectedSubcategory,
    fetchProducts,
    searchQuery,
    setSearchQuery,
    countData,
    setCountData,
    loading,
    categoriesData,
    setSelectedCategory,
    setSelectedSubcategory
  } = useContext(HerracContext);

  const { category, subcategory } = useParams();
  const [ready, setReady] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [filterOpen, setFilterOpen] = useState(false);
  
  // ✅ Local filter states (user input)
  const [localSearchQuery, setLocalSearchQuery] = useState("");
  const [localPriceMin, setLocalPriceMin] = useState("");
  const [localPriceMax, setLocalPriceMax] = useState("");
  const [localCity, setLocalCity] = useState("");
  const [localActiveOnly, setLocalActiveOnly] = useState(false);

  // ✅ Applied filter states (actual filters after clicking "Axtar")
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [appliedPriceMin, setAppliedPriceMin] = useState("");
  const [appliedPriceMax, setAppliedPriceMax] = useState("");
  const [appliedCity, setAppliedCity] = useState("");
  const [appliedActiveOnly, setAppliedActiveOnly] = useState(false);

  // Ref-lər sonsuz loop-dan qaçmaq üçün
  const isFirstRender = useRef(true);
  const hasInitialLoad = useRef(false);

  // ✅ 1. İLK YÜKLƏMƏ - component mount olduqda bütün məhsulları gətir
  useEffect(() => {
    if (hasInitialLoad.current) return;
    
    console.log("🚀 INITIAL LOAD - Fetching ALL products (active + inactive)");
    
    hasInitialLoad.current = true;
    setReady(false);
    
    // activeOnly=false göndəririk
    fetchProducts(null, null, 18, "", false)
      .then((result) => {
        console.log("✅ Initial fetch successful:", result?.length || 0, "products loaded");
        console.log("📦 Products data:", result);
        setReady(true);
      })
      .catch((error) => {
        console.error("❌ Initial fetch error:", error);
        setReady(true);
      });
  }, []);

  // ✅ 2. URL dəyişəndə kateqoriya tap
  useEffect(() => {
    if (!categoriesData || categoriesData.length === 0) return;

    console.log("🔍 Parsing URL - Category:", category, "Subcategory:", subcategory);

    const foundCategory = categoriesData.find(
      cat => formatUrlString(cat.name) === category
    );

    if (foundCategory) {
      console.log("✅ Category found:", foundCategory.name);
      setSelectedCategory(foundCategory.id);

      if (subcategory && foundCategory.subCategories) {
        const foundSubcategory = foundCategory.subCategories.find(
          sub => formatUrlString(sub.name) === subcategory
        );
        
        if (foundSubcategory) {
          console.log("✅ Subcategory found:", foundSubcategory.name);
          setSelectedSubcategory(foundSubcategory.id);
        } else {
          setSelectedSubcategory(null);
        }
      } else {
        setSelectedSubcategory(null);
      }
    } else {
      console.log("ℹ️ No category in URL - showing all products");
      setSelectedCategory(null);
      setSelectedSubcategory(null);
    }
  }, [category, subcategory, categoriesData]);

  // ✅ 3. Kateqoriya dəyişəndə fetch et
  useEffect(() => {
    // İlk render-i skip et
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    console.log("🔄 Category changed - refetching filtered products");
    console.log("Category ID:", selectedCategory);
    console.log("Subcategory ID:", selectedSubcategory);
    
    setReady(false);
    
    fetchProducts(selectedCategory, selectedSubcategory, countData, searchQuery, localActiveOnly)
      .then((result) => {
        console.log("✅ Category fetch successful:", result?.length || 0, "products");
        setReady(true);
      })
      .catch((error) => {
        console.error("❌ Category fetch error:", error);
        setReady(true);
      });
  }, [selectedCategory, selectedSubcategory]);

  // ✅ 4. Count dəyişəndə (Infinite scroll)
  useEffect(() => {
    if (countData === 18 || !hasInitialLoad.current) return;
    if (appliedSearchQuery) return; // Axtarışda scroll işləməsin
    
    console.log("📊 Loading more products - Count:", countData);
    
    fetchProducts(selectedCategory, selectedSubcategory, countData, "", appliedActiveOnly)
      .catch((error) => {
        console.error("❌ Count fetch error:", error);
      });
  }, [countData]);

  // ✅ Display məhsullar
  let displayProducts = productsData || [];
  
  console.log("📦 Current display products:", displayProducts.length);

  // Mövcud şəhərlər
  const availableCities = useMemo(() => {
    const cities = displayProducts.map(p => p.city).filter(Boolean);
    return [...new Set(cities)].sort();
  }, [displayProducts]);

  // ✅ FRONTEND FİLTRLƏMƏ - yalnız applied filtrlər tətbiq olunur
  const filteredProducts = useMemo(() => {
    let filtered = [...displayProducts];

    // Axtarış query (applied)
    if (appliedSearchQuery && appliedSearchQuery.trim()) {
      const searchLower = appliedSearchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(searchLower) ||
        p.description?.toLowerCase().includes(searchLower)
      );
    }

    // ✅ Active only (applied)
    if (appliedActiveOnly) {
      filtered = filtered.filter(p => {
        const end = new Date(p.endDate);
        return end > new Date();
      });
    }

    // Price range (applied)
    if (appliedPriceMin !== '') {
      const min = parseFloat(appliedPriceMin);
      if (!isNaN(min)) {
        filtered = filtered.filter(p => p.price >= min);
      }
    }
    
    if (appliedPriceMax !== '') {
      const max = parseFloat(appliedPriceMax);
      if (!isNaN(max)) {
        filtered = filtered.filter(p => p.price <= max);
      }
    }

    // City (applied)
    if (appliedCity) {
      filtered = filtered.filter(p => p.city === appliedCity);
    }

    console.log("🔍 Filtered products:", filtered.length);
    return filtered;
  }, [displayProducts, appliedSearchQuery, appliedActiveOnly, appliedPriceMin, appliedPriceMax, appliedCity]);

  // ✅ SORTING
  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a, b) => {
      switch (sortBy) {
        case 'ending-soon':
          return new Date(a.endDate) - new Date(b.endDate);
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'popular':
          return (b.bidCount || 0) - (a.bidCount || 0);
        case 'newest':
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });
  }, [filteredProducts, sortBy]);

  // Infinite scroll
  useInfiniteScroll(() => {
    if (!appliedSearchQuery && !loading && hasInitialLoad.current) {
      setCountData((prev) => prev + 18);
    }
  }, 0, 100);

  // Başlıq mətnləri
  const categoryText = category ? formatCategoryName(category) : "";
  const subcategoryText = subcategory ? formatCategoryName(subcategory) : "";

  // ✅ AXTAR DÜYMƏSƏ BASILANDA - filtrlər tətbiq olunur
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    
    console.log("🔍 Search button clicked");
    console.log("Applying filters:", { 
      search: localSearchQuery, 
      priceMin: localPriceMin, 
      priceMax: localPriceMax, 
      city: localCity, 
      activeOnly: localActiveOnly 
    });
    
    setReady(false);
    
    // ✅ Local filter-ləri applied-ə köçürürük
    setAppliedSearchQuery(localSearchQuery.trim());
    setAppliedPriceMin(localPriceMin);
    setAppliedPriceMax(localPriceMax);
    setAppliedCity(localCity);
    setAppliedActiveOnly(localActiveOnly);
    
    // Backend-ə yalnız search query göndəririk
    const query = localSearchQuery.trim();
    setSearchQuery(query);
    
    fetchProducts(
      selectedCategory, 
      selectedSubcategory, 
      countData, 
      query,
      false // ✅ activeOnly backend-ə false göndər, frontend-də filter edəcək
    )
      .then((result) => {
        console.log("✅ Search complete:", result?.length || 0, "products found");
        setReady(true);
      })
      .catch((error) => {
        console.error("❌ Search error:", error);
        setReady(true);
      });
  };

  // ✅ TƏMİZLƏ DÜYMƏSƏ BASILANDA
  const handleClearAll = () => {
    console.log("🧹 Clearing all filters");
    
    // Local filters təmizlə
    setLocalSearchQuery("");
    setLocalPriceMin("");
    setLocalPriceMax("");
    setLocalCity("");
    setLocalActiveOnly(false);
    
    // Applied filters təmizlə
    setAppliedSearchQuery("");
    setAppliedPriceMin("");
    setAppliedPriceMax("");
    setAppliedCity("");
    setAppliedActiveOnly(false);
    
    setSearchQuery("");
    
    // Bütün məhsulları yenidən yüklə
    setReady(false);
    fetchProducts(selectedCategory, selectedSubcategory, countData, "", false)
      .then(() => {
        console.log("✅ Filters cleared - showing all products");
        setReady(true);
      })
      .catch(() => setReady(true));
  };

  // Filter status - local changes (user typing)
  const hasLocalChanges = 
    localSearchQuery !== appliedSearchQuery ||
    localPriceMin !== appliedPriceMin ||
    localPriceMax !== appliedPriceMax ||
    localCity !== appliedCity ||
    localActiveOnly !== appliedActiveOnly;

  // Active filters (applied filters)
  const hasActiveFilters = 
    appliedPriceMin !== "" ||
    appliedPriceMax !== "" ||
    appliedCity !== "" ||
    appliedActiveOnly !== false ||
    appliedSearchQuery !== "";

  const activeFilterCount = [
    appliedPriceMin,
    appliedPriceMax,
    appliedCity,
    appliedActiveOnly ? "active" : "",
    appliedSearchQuery
  ].filter(Boolean).length;

  return (
    <div className="hrrc-auction-wrapper">
      {ready && (
        <div className="hrrc-header-card">
          <div className="hrrc-header-top">
            <div className="hrrc-header-left">
              <div className="hrrc-gavel-icon-wrapper">
                <FaGavel className="hrrc-gavel-icon" />
              </div>
              <div className="hrrc-title-section">
                <h1 className="hrrc-main-title">
                  {appliedSearchQuery ? (
                    <>
                      <FaSearch className="hrrc-title-icon" />
                      Axtarış nəticələri
                    </>
                  ) : subcategory ? (
                    `${categoryText} > ${subcategoryText}`
                  ) : category ? (
                    categoryText
                  ) : (
                    "Bütün Hərraclar"
                  )}
                </h1>
                {appliedSearchQuery && (
                  <p className="hrrc-subtitle">
                    "{capitalizeFirstLetter(appliedSearchQuery)}" üzrə tapılıb
                  </p>
                )}
              </div>
            </div>

            <div className="hrrc-view-controls">
              <button
                className={`hrrc-view-btn ${viewMode === 'grid' ? 'hrrc-active' : ''}`}
                onClick={() => setViewMode('grid')}
                title="Grid görünüşü"
              >
                <FaTh />
              </button>
              <button
                className={`hrrc-view-btn ${viewMode === 'list' ? 'hrrc-active' : ''}`}
                onClick={() => setViewMode('list')}
                title="List görünüşü"
              >
                <FaThList />
              </button>
            </div>
          </div>

          <form onSubmit={handleSearchSubmit} className="hrrc-search-form">
            <div className="hrrc-search-container">
              <div className="hrrc-search-input-wrapper">
                <FaSearch className="hrrc-search-icon" />
                <input
                  type="text"
                  placeholder="Elan axtar... (məsələn: Niva, iPhone, mənzil)"
                  value={localSearchQuery}
                  onChange={(e) => setLocalSearchQuery(e.target.value)}
                  className="hrrc-search-input"
                />
                {localSearchQuery && (
                  <button
                    type="button"
                    onClick={() => setLocalSearchQuery("")}
                    className="hrrc-search-clear"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>

            <div className="hrrc-controls-row">
              <div className="hrrc-left-controls">
                <div className="hrrc-results-badge">
                  <span className="hrrc-results-number">{sortedProducts.length}</span>
                  <span className="hrrc-results-text">nəticə</span>
                </div>

                <button 
                  type="button"
                  className={`hrrc-filter-btn ${filterOpen ? 'hrrc-open' : ''} ${hasActiveFilters ? 'hrrc-has-filters' : ''}`}
                  onClick={() => setFilterOpen(!filterOpen)}
                >
                  <FaFilter />
                  <span>Filtrlər</span>
                  {hasActiveFilters && (
                    <span className="hrrc-filter-badge">{activeFilterCount}</span>
                  )}
                  <FaChevronDown className={`hrrc-chevron ${filterOpen ? 'hrrc-rotated' : ''}`} />
                </button>
              </div>

              <div className="hrrc-sort-container">
                <FaSortAmountDown className="hrrc-sort-icon" />
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="hrrc-sort-select"
                >
                  <option value="newest">Ən yeni</option>
                  <option value="ending-soon">Tezliklə bitən</option>
                  <option value="popular">Ən məşhur</option>
                  <option value="price-low">Qiymət: Aşağıdan yuxarı</option>
                  <option value="price-high">Qiymət: Yuxarıdan aşağı</option>
                </select>
              </div>
            </div>

            {filterOpen && (
              <div className="hrrc-filters-panel">
                <div className="hrrc-filters-container">
                  <div className="hrrc-filter-group">
                    <label className="hrrc-filter-label">
                      <FaDollarSign className="hrrc-label-icon" />
                      <span>Qiymət aralığı</span>
                    </label>
                    <div className="hrrc-price-inputs">
                      <div className="hrrc-input-wrapper">
                        <input
                          type="number"
                          placeholder="Min (AZN)"
                          value={localPriceMin}
                          onChange={(e) => setLocalPriceMin(e.target.value)}
                          className="hrrc-price-input"
                          min="0"
                        />
                      </div>
                      <span className="hrrc-price-separator">—</span>
                      <div className="hrrc-input-wrapper">
                        <input
                          type="number"
                          placeholder="Max (AZN)"
                          value={localPriceMax}
                          onChange={(e) => setLocalPriceMax(e.target.value)}
                          className="hrrc-price-input"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="hrrc-filter-group">
                    <label className="hrrc-filter-label">
                      <FaMapMarkerAlt className="hrrc-label-icon" />
                      <span>Şəhər</span>
                    </label>
                    <div className="hrrc-select-wrapper">
                      <select
                        value={localCity}
                        onChange={(e) => setLocalCity(e.target.value)}
                        className="hrrc-city-select"
                      >
                        <option value="">Bütün şəhərlər</option>
                        {availableCities.map(city => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="hrrc-filter-group hrrc-checkbox-group">
                    <label className="hrrc-checkbox-label">
                      <input
                        type="checkbox"
                        checked={localActiveOnly}
                        onChange={(e) => setLocalActiveOnly(e.target.checked)}
                        className="hrrc-checkbox-input"
                      />
                      <span className="hrrc-checkbox-custom">
                        {localActiveOnly && <FaCheckCircle />}
                      </span>
                      <span className="hrrc-checkbox-text">Yalnız aktiv hərraclar</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            <div className="hrrc-actions-row">
              <button 
                type="submit" 
                className="hrrc-search-btn"
                disabled={!hasLocalChanges && !hasActiveFilters}
              >
                <FaSearch />
                <span>Axtar</span>
              </button>
              
              {(hasActiveFilters || hasLocalChanges) && (
                <button 
                  type="button"
                  onClick={handleClearAll}
                  className="hrrc-clear-btn"
                >
                  <FaTimes />
                  <span>Təmizlə</span>
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {loading || !ready ? (
        <Loading />
      ) : (
        <div className="hrrc-content">
          <div className={`hrrc-products-grid hrrc-mode-${viewMode}`}>
            {sortedProducts.length > 0 ? (
              sortedProducts.map((item) => (
                <Announcement key={item.id} {...item} />
              ))
            ) : (
              <div className="hrrc-empty-state">
                <div className="hrrc-empty-content">
                  <div className="hrrc-empty-icon-wrapper">
                    <FaSearch className="hrrc-empty-icon" />
                  </div>
                  <h3 className="hrrc-empty-title">Nəticə tapılmadı</h3>
                  <p className="hrrc-empty-text">
                    {hasActiveFilters 
                      ? "Filtr parametrlərinizi dəyişdirərək yenidən cəhd edin"
                      : "Axtarış kriteriyalarınızı dəyişdirərək yenidən cəhd edin"
                    }
                  </p>
                  {hasActiveFilters && (
                    <button 
                      className="hrrc-empty-btn" 
                      onClick={handleClearAll}
                    >
                      Filtrləri təmizlə
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Announcements;